/* eslint-disable max-lines */
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Days from "../../../components/lines-managements/trips/Days";
import EnhancedDayPicker, { SelectedDateType } from "../../../components/lines-managements/trips/EnhancedDayPicker";
import CreateTripWrapper from "../../../components/lines-managements/trips/schedules/CreateTripWrapper";
import Box from "../../../components/ui/Box";
import Button from "../../../components/ui/Button";
import SpinnerLoader from "../../../components/ui/SpinnerLoader";
import { BulkUpdateLocationState, TripPreviewRow } from "../../../interfaces/endpoints/trips/IBulkEditTripsWorkingDays";
import { CustomTripWorkingDayType, IOperationalInterval } from "../../../interfaces/endpoints/trips/Trips.types";
import { bulkEditTripsWorkingDays } from "../../../services/Trips.service";
import "../../../styles/shared-calendar.css";

// Render operational interval like in Trips list
const renderOperationalInterval = (row: TripPreviewRow): React.ReactNode => {
  if (row.hasCustomDates && row.customWorkingDays && row.customWorkingDays.length > 0) {
    const exceptionDates = row.customWorkingDays.filter(
      (d) => d.type === CustomTripWorkingDayType.ExceptionDate
    );
    const workingDates = row.customWorkingDays.filter(
      (d) => d.type === CustomTripWorkingDayType.SelectedWorkingDate
    );

    return (
      <div className="space-y-1">
        <span className="text-xs text-gray-600 italic block">
          Ka data specifike të punës
        </span>
        <div className="flex flex-wrap gap-1">
          {exceptionDates.length > 0 && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs
                font-medium bg-[#fef7f5] text-[#f05a2d]"
            >
              Përjashtimet: {format(new Date(exceptionDates[0].date), "dd/MM/yyyy")}
              {exceptionDates.length > 1 && (
                <span className="ml-1 text-gray-500">
                  +{exceptionDates.length - 1}
                </span>
              )}
            </span>
          )}
          {workingDates.length > 0 && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs
                font-medium bg-[#fef7f5] text-[#f05a2d]"
            >
              Datat e punës: {format(new Date(workingDates[0].date), "dd/MM/yyyy")}
              {workingDates.length > 1 && (
                <span className="ml-1 text-gray-500">
                  +{workingDates.length - 1}
                </span>
              )}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <Days
      days={[
        row.operationalInterval.monday,
        row.operationalInterval.tuesday,
        row.operationalInterval.wednesday,
        row.operationalInterval.thursday,
        row.operationalInterval.friday,
        row.operationalInterval.saturday,
        row.operationalInterval.sunday
      ]}
    />
  );
};

const BulkUpdateTrips = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as BulkUpdateLocationState | null;

  // Track selected trip IDs (allows deselection)
  const [selectedTripIds, setSelectedTripIds] = useState<Set<number>>(
    () => new Set(state?.tripIds ?? [])
  );

  // Working days type (ExceptionDate vs. SelectedWorkingDate)
  const [workingDaysType, setWorkingDaysType] = useState<CustomTripWorkingDayType>(
    CustomTripWorkingDayType.ExceptionDate
  );

  // Operational interval (weekly days)
  const [operationalInterval, setOperationalInterval] = useState<IOperationalInterval>({
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: true,
    sunday: true
  });

  // Custom dates selection
  const [selectedDatesCustom, setSelectedDatesCustom] = useState<SelectedDateType[]>([]);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle trip selection toggle
  const handleTripToggle = useCallback((tripId: number) => {
    setSelectedTripIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tripId)) {
        newSet.delete(tripId);
      } else {
        newSet.add(tripId);
      }
      return newSet;
    });
  }, []);

  // Handle select/deselect all trips
  const handleToggleAll = useCallback(() => {
    if (!state?.tripIds) return;
    setSelectedTripIds((prev) => {
      if (prev.size === state.tripIds.length) {
        return new Set();
      }
      return new Set(state.tripIds);
    });
  }, [state?.tripIds]);

  // Handle operational interval checkbox change
  const handleDayChange = useCallback((day: keyof IOperationalInterval) => {
    setOperationalInterval((prev) => ({
      ...prev,
      [day]: !prev[day]
    }));
  }, []);

  // Handle date selection change
  const handleDateSelectionChange = useCallback((dates: SelectedDateType[]) => {
    setSelectedDatesCustom(dates);
  }, []);

  // Handle working days type change
  const handleWorkingDaysTypeChange = useCallback((newType: CustomTripWorkingDayType) => {
    setWorkingDaysType(newType);
    // Clear selected dates when switching type
    setSelectedDatesCustom([]);
  }, []);

  // Submit bulk update
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedTripIds.size === 0) {
      toast.error("Nuk ka orare të zgjedhura për përditësim.");
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare custom working days with UTC dates
      const customWorkingDays = selectedDatesCustom.map((d) => ({
        type: workingDaysType,
        date: new Date(Date.UTC(
          d.date.getFullYear(),
          d.date.getMonth(),
          d.date.getDate(),
          12, 0, 0, 0
        ))
      }));

      const tripIdsArray = Array.from(selectedTripIds);

      await bulkEditTripsWorkingDays({
        tripIds: tripIdsArray,
        operationalInterval,
        customWorkingDays
      });

      toast.success(
        `${tripIdsArray.length} orare u përditësuan me sukses.`,
        { position: "top-right" }
      );

      // Navigate back to trips list
      setTimeout(() => {
        navigate("/trips", { state: ["Menaxhimi i linjave", "Oraret"] });
      }, 500);
    } catch (error) {
      console.error("Bulk update failed:", error);
      toast.error("Ndodhi një gabim gjatë përditësimit të orareve.", { position: "top-right" });
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedTripIds, operationalInterval, selectedDatesCustom, workingDaysType, navigate]);

  // If no state is passed, show empty state
  if (!state?.tripIds || state.tripIds.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <h2 className="text-xl font-semibold text-gray-700 mb-4">
          Nuk ka orare të zgjedhura
        </h2>
        <p className="text-gray-500 mb-6">
          Ju lutem zgjidhni oraret që dëshironi të përditësoni nga faqja e orareve.
        </p>
        <Link
          to="/trips"
          className="flex items-center text-primary hover:underline"
          state={["Menaxhimi i linjave", "Oraret"]}
        >
          <ChevronLeftIcon className="w-4 h-4 mr-1" />
          Kthehu tek oraret
        </Link>
      </div>
    );
  }

  return (
    <div>
      <Link
        className="flex items-center mb-4 text-sm font-semibold text-primary"
        to="/trips"
        state={["Menaxhimi i linjave", "Oraret"]}
      >
        <ChevronLeftIcon className="w-4 h-4 mr-2" />
        <span>Kthehu tek oraret</span>
      </Link>

      <Box className="p-0">
        <form onSubmit={handleSubmit} className="flex flex-col">
          <h1 className="text-xl mb-2 py-3 px-5 border-b">
            Përditëso {selectedTripIds.size} orare
          </h1>

          <div className="p-5 overflow-auto">
            {/* Selected trips preview */}
            <CreateTripWrapper title="Oraret e zgjedhura" className="border-none mb-6">
              <div className="max-h-72 overflow-y-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="py-2 px-3 w-10">
                        <input
                          type="checkbox"
                          className="cursor-pointer"
                          checked={selectedTripIds.size === state.tripIds.length}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = selectedTripIds.size > 0 &&
                              selectedTripIds.size < state.tripIds.length;
                            }
                          }}
                          onChange={handleToggleAll}
                          title={selectedTripIds.size === state.tripIds.length ?
                             "Çzgjidh të gjitha" : "Zgjidh të gjitha"}
                        />
                      </th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Linja</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Orari</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Ditët e udhëtimit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {state.tripPreviewRows.map((row) => (
                      <tr
                        key={row.tripId}
                        className={`hover:bg-gray-50 cursor-pointer ${
                          !selectedTripIds.has(row.tripId) ? "opacity-50" : ""
                        }`}
                        onClick={() => handleTripToggle(row.tripId)}
                      >
                        <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="cursor-pointer"
                            checked={selectedTripIds.has(row.tripId)}
                            onChange={() => handleTripToggle(row.tripId)}
                          />
                        </td>
                        <td className="py-2 px-3">{row.routeName}</td>
                        <td className="py-2 px-3 whitespace-nowrap">
                          {row.departureTimeString} - {row.arrivalTimeString}
                        </td>
                        <td className="py-2 px-3">
                          {renderOperationalInterval(row)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CreateTripWrapper>

            {/* Working days selection */}
            <CreateTripWrapper title="Ditët e udhëtimit" className="border-none mt-5">
              <div className="space-y-4">
                <div className="flex gap-6 md:px-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="workingDaysType"
                      checked={workingDaysType === CustomTripWorkingDayType.ExceptionDate}
                      onChange={() =>
                        handleWorkingDaysTypeChange(CustomTripWorkingDayType.ExceptionDate)
                      }
                      className="w-4 h-4 accent-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="ml-2 text-gray-700">Përsëritje javore me përjashtime</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="workingDaysType"
                      checked={workingDaysType === CustomTripWorkingDayType.SelectedWorkingDate}
                      onChange={() =>
                        handleWorkingDaysTypeChange(CustomTripWorkingDayType.SelectedWorkingDate)
                      }
                      className="w-4 h-4 accent-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="ml-2 text-gray-700">Datat specifike të punës</span>
                  </label>
                </div>

                <div>
                  {workingDaysType === CustomTripWorkingDayType.ExceptionDate && (
                    <div className="flex-wrap flex gap-7 md:gap-0">
                      <div className="md:px-4 flex items-center">
                        <input
                          type="checkbox"
                          id="monday"
                          className="cursor-pointer"
                          checked={operationalInterval.monday}
                          onChange={() => handleDayChange("monday")}
                        />
                        <label
                          htmlFor="monday"
                          className="ml-3 text-sm text-neutral cursor-pointer"
                        >
                          E Hënë
                        </label>
                      </div>
                      <div className="md:px-4 flex items-center">
                        <input
                          type="checkbox"
                          id="tuesday"
                          className="cursor-pointer"
                          checked={operationalInterval.tuesday}
                          onChange={() => handleDayChange("tuesday")}
                        />
                        <label
                          htmlFor="tuesday"
                          className="ml-3 text-sm text-neutral cursor-pointer"
                        >
                          E Martë
                        </label>
                      </div>
                      <div className="md:px-4 flex items-center">
                        <input
                          type="checkbox"
                          id="wednesday"
                          className="cursor-pointer"
                          checked={operationalInterval.wednesday}
                          onChange={() => handleDayChange("wednesday")}
                        />
                        <label
                          htmlFor="wednesday"
                          className="ml-3 text-sm text-neutral cursor-pointer"
                        >
                          E Mërkurë
                        </label>
                      </div>
                      <div className="md:px-4 flex items-center">
                        <input
                          type="checkbox"
                          id="thursday"
                          className="cursor-pointer"
                          checked={operationalInterval.thursday}
                          onChange={() => handleDayChange("thursday")}
                        />
                        <label
                          htmlFor="thursday"
                          className="ml-3 text-sm text-neutral cursor-pointer"
                        >
                          E Enjte
                        </label>
                      </div>
                      <div className="md:px-4 flex items-center">
                        <input
                          type="checkbox"
                          id="friday"
                          className="cursor-pointer"
                          checked={operationalInterval.friday}
                          onChange={() => handleDayChange("friday")}
                        />
                        <label
                          htmlFor="friday"
                          className="ml-3 text-sm text-neutral cursor-pointer"
                        >
                          E Premte
                        </label>
                      </div>
                      <div className="md:px-4 flex items-center">
                        <input
                          type="checkbox"
                          id="saturday"
                          className="cursor-pointer"
                          checked={operationalInterval.saturday}
                          onChange={() => handleDayChange("saturday")}
                        />
                        <label
                          htmlFor="saturday"
                          className="ml-3 text-sm text-neutral cursor-pointer"
                        >
                          E Shtunë
                        </label>
                      </div>
                      <div className="md:px-4 flex items-center">
                        <input
                          type="checkbox"
                          id="sunday"
                          className="cursor-pointer"
                          checked={operationalInterval.sunday}
                          onChange={() => handleDayChange("sunday")}
                        />
                        <label
                          htmlFor="sunday"
                          className="ml-3 text-sm text-neutral cursor-pointer"
                        >
                          E Diel
                        </label>
                      </div>
                    </div>
                  )}
                  <div className="w-full flex justify-center mt-4">
                    <div className="w-[350px]">
                      <EnhancedDayPicker
                        selectedDates={selectedDatesCustom}
                        onChange={handleDateSelectionChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CreateTripWrapper>
          </div>

          {/* Footer with submit button */}
          <div className="border-t p-4 justify-end flex sticky bottom-0 rounded-b-lg bg-white z-10">
            <div>
              <Button
                btnType="custom"
                className="border border-primary text-primary mr-4"
                onClick={() => navigate(-1)}
                disabled={isSubmitting}
              >
                Anulo
              </Button>
              <Button type="submit" disabled={isSubmitting || selectedTripIds.size === 0}>
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <SpinnerLoader />
                    Duke përditësuar...
                  </span>
                ) : (
                  "Ruaj ndryshimet"
                )}
              </Button>
            </div>
          </div>
        </form>
      </Box>
    </div>
  );
};

export default BulkUpdateTrips;
